import '../networkDetector.platform';

import type { ClientState } from '../types';
import { PersistentStateLoadingState } from '../types';
import { isBookwise, isMobile } from '../utils/environment';
import exceptionHandler from '../utils/exceptionHandler.platform';
import makeLogger from '../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import database from './database';
import { updatePersistentStateLoadingState } from './methods';
import onDatabaseSchemaConflict from './onDatabaseSchemaConflict';
// eslint-disable-next-line import/no-cycle
import onLoggedOut from './onLoggedOut.platform';
// eslint-disable-next-line import/no-cycle
import background from './portalGates/toBackground';
import { rxAlert } from './rxAlert.platform';
// eslint-disable-next-line import/no-cycle
import { initClientData } from './stateUpdaters/clientStateUpdaters/readerSettings';
// eslint-disable-next-line import/no-cycle
import { initPersistentState } from './stateUpdaters/persistentStateUpdaters/infrastructureRelated';

const logger = makeLogger(__filename);

const getAndSetClientState = async () => {
  const clientData = await background.getCacheItem<ClientState>('clientData');
  await initClientData(clientData);
};

export default async function setUpStateTree(): Promise<void> {
  let setUpInitialStateResult: Awaited<ReturnType<typeof background.setUpInitialState>>;

  try {
    logger.debug('Start: background.setUpInitialState');
    await updatePersistentStateLoadingState(PersistentStateLoadingState.HasNotStarted);
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `updatePersistentStateLoadingState passed`,
      });
    }
    await database.initialize({ onSchemaConflictError: onDatabaseSchemaConflict });
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `database.initialize passed`,
      });
    }
    // Pull all our cached data from disk. Skip the list cache and clientData if we're minimizing state.
    logger.debug('Start: fetching');

    getAndSetClientState();

    // Quick and easy check for if the user has access to Reader.
    // We use .catch here so that initialization doesn't block on getAndSetProfileData:
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    background.getAndSetProfileData().catch((e: any) => {
      if (e?.response?.status === 401) {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `onLoggedOut`,
          });
        }
        onLoggedOut();
      } else {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `background.getAndSetProfileData().catch throwed`,
            data: e,
          });
        }
        throw e;
      }
    });


    setUpInitialStateResult = await background.setUpInitialState();
    logger.debug('End: background.setUpInitialState');
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `setUpStateTree End: background.setUpInitialState`,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `setUpStateTree errored`,
      });
    }
    await updatePersistentStateLoadingState(PersistentStateLoadingState.Failed);
    if (['Could not establish connection. Receiving end does not exist.'].includes(err.message)) {
      logger.error('error setting up initial state', { err });
      return;
    } else {
      if (err.response?.status === 401 || err.message?.includes('401 for')) {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `setUpStateTree onLoggedOut`,
          });
        }
        onLoggedOut();
      } else if (err.reason) {
        if (isMobile) {
          exceptionHandler.addBreadcrumb({
            type: 'debug',
            message: `error setting up initial state`,
            data: err,
          });
        }
        logger.error('error setting up initial state', { err });
        exceptionHandler.captureException(err);
        rxAlert(
          `Couldn't initialize the app. Would you like to clear your local data and restart? \n\n Reason: ${err.reason}`,
          async () => {
            await database.clearUninitializedDb();
          },
        );
      } else {
        let app = 'Reader';
        if (isBookwise) {
          app = 'Bookwise';
        }
        logger.error('error setting up initial state', { err });
        exceptionHandler.captureException(err);
        rxAlert(
          `We've updated ${app}\n\nTo load the latest version of ${app} (with our latest bug fixes and features), please upgrade by hitting OK below.\n\nYou may need to refresh the tab one more time after this (and close any other open tabs) to finish the update. If you have any questions, please shoot us a quick email at hello@readwise.io :)`,
          async () => background.clearAllLocalData(),
        );
      }
      return;
    }
  }

  if (!setUpInitialStateResult) {
    if (isMobile) {
      exceptionHandler.addBreadcrumb({
        type: 'debug',
        message: `setUpStateTree: !setUpInitialStateResult`,
      });
    }
    // eslint-disable-next-line no-alert
    alert(`Unknown error in initializing the app`);
    return;
  }

  await initPersistentState(setUpInitialStateResult.persistentState);
  if (isMobile) {
    exceptionHandler.addBreadcrumb({
      type: 'debug',
      message: `setUpStateTree initPersistentState passed`,
    });
  }
  await background.pollLatestState(15);

  // kick off backend document syncs on page load, so we can get the latest articles
  setTimeout(async () => {
    await background.triggerCloudSyncs().catch((e) => {
      if (isMobile) {
        exceptionHandler.addBreadcrumb({
          type: 'debug',
          message: `background.triggerCloudSyncs() throwed`,
          data: e,
        });
      }
    });
  }, 1000);

}
